// In-app dossier shop — the primary market (buy sealed dossiers direct from
// the registry), brought into the signed-in SPA. Reuses the public
// GET /api/v1/shop/drops browse endpoint; the highlighted drop's creator
// roster (portraits + handles) is fetched from the per-drop detail endpoint
// (GET /api/v1/shop/drops/:id) for the hero marquee.
//
// Epic 8 design pass (2026-08): recreated pixel-for-pixel from the Greenhold
// Shop handoff (design/claude-design/design_handoff_shop_market) — light
// paper page, dark-forest "highlighted dossier" hero with a creator marquee,
// a standing-dossiers grid that swaps into the hero on select, the welcome
// free-claim card, and the registry-voiced buy modal. All prices/currency
// are the app's REAL per-drop values (€, from price_cents).

import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";

import { Button, SealedDossier, formatIssueNumber } from "@greenhold/components";

import { EmptyState } from "../components/EmptyState";
import { HighlightedDossier } from "../features/shop/HighlightedDossier";
import { HighlightedDossierModal } from "../features/shop/HighlightedDossierModal";
import { useShopDrops, type ShopDropResponse } from "../features/shop/useShopDrops";
import { useFreeClaim } from "../features/free-claim/useFreeClaim";
import { useClaimFreeDossier } from "../features/free-claim/useClaimFreeDossier";

const EUR = new Intl.NumberFormat("en-IE", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 2,
});

function formatEur(cents: number): string {
  return EUR.format(cents / 100);
}

function dropMeta(drop: ShopDropResponse): string {
  const parts = [
    "open issue",
    `${drop.creator_count} creator${drop.creator_count === 1 ? "" : "s"}`,
  ];
  const theme = drop.portfolio_theme?.trim();
  if (theme) parts.push(theme);
  return parts.join(" · ");
}

export function ShopPage() {
  const query = useShopDrops();
  const freeClaim = useFreeClaim();
  const claimMutation = useClaimFreeDossier();

  // Mirror the Astro storefront mapping: the public shop never renders the
  // perpetual "welcome" drop, so keep only regular/special.
  const drops = useMemo<ShopDropResponse[]>(
    () =>
      (query.data ?? []).filter(
        (d) => d.kind === "regular" || d.kind === "special",
      ),
    [query.data],
  );

  // The standing-card pop-up: the clicked drop shown in the identical hero
  // presentation, or null when closed.
  const [modalDrop, setModalDrop] = useState<ShopDropResponse | null>(null);

  // Stalled dossiers are NEVER featured — they stay in the standing
  // grid (greyed) and can't be promoted into the hero. The highlighted
  // dossier is the admin-chosen head of the featurable list; clicking a
  // standing card opens it in a pop-up rather than swapping this hero.
  const featurable = drops.filter((d) => !d.stalled);
  const selected = featurable[0] ?? null;

  const standing = drops.filter((d) => d.drop_id !== selected?.drop_id);

  // Free-claim slot state (migration 0047 — weekly free claim).
  //   "claimable"        → full dashed-gold claim card;
  //   "claimed"          → slim bar with the live "next free claim" countdown;
  //   "nothing_to_claim" → honest line, no countdown.
  // While the query is pending we render nothing so the slot doesn't flash
  // the wrong state.
  const freeClaimState = freeClaim.data?.data.state;
  const nextResetAt = freeClaim.data?.data.next_reset_at;

  const handleClaim = () => {
    claimMutation.mutate(undefined, {
      onSuccess: (env) => {
        if (env.data.state === "claimed") {
          // Say WHICH dossier the random draw granted (title + issue) —
          // the generic line is the fallback for idempotent re-claims,
          // where the mint fields are null.
          const title = env.data.claimed_portfolio_title;
          const edition = env.data.claimed_edition_number;
          toast.success(
            title != null && edition != null
              ? `You drew "${title}" — Issue #${formatIssueNumber(edition)}, sealed in your collection.`
              : "Free dossier claimed — it's sealed in your collection.",
            { duration: 6000 },
          );
        } else if (env.data.state === "nothing_to_claim") {
          toast("No dossiers are live to claim right now.");
        }
      },
      onError: (error) => {
        toast.error(error.message);
      },
    });
  };

  return (
    <div className="min-h-[calc(100vh-3.5rem)] bg-brand-paper-200 text-brand-forest-900">
      <main className="mx-auto grid w-full max-w-[1080px] gap-7 px-6 pt-8 pb-16">
        {/* 1 — Title block */}
        <div>
          <h1 className="m-0 font-heading text-[1.75rem] font-bold">Shop</h1>
        </div>

        {query.isLoading ? (
          <div
            className="flex items-center justify-center py-24 text-brand-forest-900/60"
            role="status"
          >
            <span className="font-body text-sm">Loading the shop…</span>
          </div>
        ) : query.isError ? (
          <div
            className="mx-auto max-w-md rounded-lg border border-state-error-accent/50 bg-brand-paper-50 px-6 py-10 text-center"
            role="alert"
          >
            <h2 className="m-0 font-heading text-xl text-brand-forest-900">
              We couldn't load the shop
            </h2>
            <p className="mx-auto mt-2 max-w-sm font-body text-sm text-brand-forest-900/70">
              Something went wrong fetching the current dossiers.
            </p>
            <div className="mt-4">
              <Button variant="secondary" size="md" onClick={() => query.refetch()}>
                Try again
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* 2 — Highlighted dossier hero (admin-chosen head of the
                featurable list). The same presentation renders in the
                standing-card pop-up below via <HighlightedDossier>. */}
            {selected ? <HighlightedDossier drop={selected} /> : null}

            {/* 3 — Free claim slot — ALWAYS present between the hero and the
                standing grid once the free-claim query resolves (migration
                0047 — weekly free claim, resets Wednesday 11:00 Copenhagen).
                claimable        → full dashed-gold claim card;
                claimed          → slim bar with the live countdown to the
                                   next reset (next_reset_at from the API);
                nothing_to_claim → honest line, no countdown.
                Loading → nothing (no flash). */}
            {freeClaimState === "claimable" ? (
              <div
                className="flex flex-wrap items-center gap-6 rounded-xl border border-dashed border-brand-gold-400 bg-brand-paper-50 px-7 py-6"
                data-testid="shop-free-claim"
              >
                <div className="pointer-events-none w-[90px] shrink-0">
                  <SealedDossier size="thumb" title="Unmarked" />
                </div>
                <div className="grid min-w-[240px] flex-1 gap-1">
                  <span className="font-heading text-[0.95rem] font-semibold">
                    Free claim
                  </span>
                  <span className="text-[0.82rem] opacity-70">
                    One free dossier a week, drawn from the live drops. Sealed
                    like any other — the registry records the pour when you
                    open it in your collection.
                  </span>
                </div>
                <Button
                  variant="secondary"
                  size="md"
                  onClick={handleClaim}
                  disabled={claimMutation.isPending}
                >
                  {claimMutation.isPending ? "Claiming…" : "Claim free dossier"}
                </Button>
              </div>
            ) : freeClaimState === "claimed" ? (
              <div
                className="flex items-center justify-center rounded-xl border border-dashed border-brand-paper-300 px-6 py-3 text-center"
                data-testid="shop-free-claim-used"
              >
                <span className="font-mono text-[0.72rem] text-brand-forest-900/55">
                  Next free claim available in{" "}
                  <FreeClaimCountdown targetIso={nextResetAt} />
                </span>
              </div>
            ) : freeClaimState === "nothing_to_claim" ? (
              <div
                className="flex items-center justify-center rounded-xl border border-dashed border-brand-paper-300 px-6 py-3 text-center"
                data-testid="shop-free-claim-empty"
              >
                <span className="font-mono text-[0.72rem] text-brand-forest-900/55">
                  No dossiers are live to claim right now ·{" "}
                  <span className="text-brand-gold-600">check back soon.</span>
                </span>
              </div>
            ) : null}

            {/* 4 — Standing dossiers */}
            {drops.length === 0 ? (
              <EmptyState
                aria-label="No dossiers released"
                title="No dossiers released yet"
                description="The first sealed dossier will appear here the moment it's released. Follow the feed for the go-live announcement."
                className="mx-auto max-w-xl"
              />
            ) : standing.length > 0 ? (
              <div>
                <h2 className="m-0 mb-3.5 font-heading text-[1.05rem]">
                  Standing dossiers
                </h2>
                <div className="grid gap-6 [grid-template-columns:repeat(auto-fill,minmax(300px,1fr))]">
                  {standing.map((d) => {
                    const cardBody = (
                      <>
                        <div className="pointer-events-none w-[120px] shrink-0">
                          <SealedDossier
                            size="md"
                            title={d.portfolio_title}
                            game={d.portfolio_theme ?? undefined}
                            showRetirementWarning={d.status === "retiring"}
                            sealColor={d.portfolio_seal_color}
                          />
                        </div>
                        <div className="grid min-w-0 gap-1.5">
                          <span className="line-clamp-2 min-w-0 break-words font-heading text-[0.95rem] font-semibold">
                            {d.portfolio_title}
                          </span>
                          <span className="min-w-0 truncate font-mono text-[0.62rem] uppercase tracking-[0.1em] opacity-55">
                            {dropMeta(d)}
                          </span>
                          <span className="mt-1 font-mono text-[0.95rem] font-semibold">
                            {formatEur(d.price_cents)}
                          </span>
                          {d.stalled ? (
                            <p className="m-0 mt-1 font-body text-[0.75rem] italic opacity-80">
                              This dossier is temporarily unavailable while its
                              creator roster is being updated. Sorry for any
                              inconvenience.
                            </p>
                          ) : (
                            // Visual affordance only — the whole card is the
                            // single interactive element (nesting a real
                            // button inside a button is invalid). Clicking it
                            // opens the drop in the identical highlight pop-up;
                            // purchase happens there.
                            <div className="mt-1">
                              <Button asChild variant="secondary" size="sm">
                                <span>View</span>
                              </Button>
                            </div>
                          )}
                        </div>
                      </>
                    );

                    const cardClass =
                      "flex items-center gap-5 rounded-lg border border-brand-paper-300 bg-brand-paper-50 p-5";

                    // Stalled = consent-withdrawal threshold tripped; visible
                    // but greyed + unbuyable, so it stays a plain, inert card.
                    if (d.stalled) {
                      return (
                        <div
                          key={d.drop_id}
                          className={`${cardClass} opacity-60 grayscale`}
                          data-testid="shop-stalled-card"
                        >
                          {cardBody}
                        </div>
                      );
                    }

                    // Live cards: the whole card opens the highlight pop-up,
                    // keyboard-accessible via the native button (parity with
                    // the vault collection grid).
                    return (
                      <button
                        key={d.drop_id}
                        type="button"
                        onClick={() => setModalDrop(d)}
                        aria-label={`View ${d.portfolio_title}`}
                        data-testid="shop-standing-card"
                        className={`${cardClass} w-full text-left transition-colors hover:border-brand-gold-500/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-forest-500 focus-visible:ring-offset-2`}
                      >
                        {cardBody}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null}

            {/* 5 — Footer line */}
            <p className="m-0 text-center text-[0.75rem] opacity-55">
              Every purchase is registry-recorded at pour. No refunds after the
              seal is broken.
            </p>
          </>
        )}
      </main>

      <HighlightedDossierModal
        drop={modalDrop}
        onClose={() => setModalDrop(null)}
      />
    </div>
  );
}

/** Live countdown to the next weekly free-claim reset. Ticks every second,
 *  gold-600 mono per the shop handoff. For waits over 48h it prefixes the
 *  day count: "Xd HH:MM:SS"; otherwise "HH:MM:SS". */
function FreeClaimCountdown({ targetIso }: { targetIso: string | undefined }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  if (!targetIso) return null;
  const target = new Date(targetIso).getTime();
  const remainingMs = Math.max(0, target - now);
  const totalSeconds = Math.floor(remainingMs / 1000);
  const days = Math.floor(totalSeconds / 86_400);
  const hours = Math.floor((totalSeconds % 86_400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n: number) => n.toString().padStart(2, "0");
  const hms = `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  // Over 48h → show the day count too.
  const label = totalSeconds >= 48 * 3600 ? `${days}d ${hms}` : hms;

  return (
    <span className="tabular-nums text-brand-gold-600" data-testid="free-claim-countdown">
      {label}
    </span>
  );
}
